/**
 * Slot Helper Utilities
 *
 * Pure helper functions for slot data formatting and validation.
 * All functions are side-effect free and can be tested in isolation.
 */

/**
 * Convert date from database format (yyyy-mm-dd) to Israeli display format (dd.mm.yyyy)
 *
 * @param dateStr - Date string in yyyy-mm-dd format
 * @returns Date string in dd.mm.yyyy format
 *
 * @example
 * formatDisplayDate('2025-10-15') // Returns: '15.10.2025'
 */
export const formatDisplayDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-')
  return `${day}.${month}.${year}`
}

/**
 * Add Hebrew "יום" prefix to day names for proper Hebrew grammar
 *
 * @param day - Hebrew day name (e.g., 'ראשון', 'שני')
 * @returns Day name with "יום" prefix if applicable
 *
 * @example
 * formatDayWithPrefix('ראשון') // Returns: 'יום ראשון'
 * formatDayWithPrefix('other') // Returns: 'other'
 */
export const formatDayWithPrefix = (day: string): string => {
  const hebrewDays = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת']
  return hebrewDays.includes(day) ? `יום ${day}` : day
}

/**
 * Mask email address for privacy display (show first character and domain only)
 *
 * @param email - Full email address
 * @returns Masked email address
 *
 * @example
 * maskEmail('john.doe@example.com') // Returns: 'j***@example.com'
 */
export const maskEmail = (email: string): string => {
  const [username, domain] = email.split('@')
  return `${username.slice(0, 1)}***@${domain}`
}

/**
 * Remove "כיתה " prefix from class names for compact display
 *
 * @param className - Full class name with prefix
 * @returns Class name without prefix
 *
 * @example
 * removeClassPrefix('כיתה א-1') // Returns: 'א-1'
 * removeClassPrefix('א-1') // Returns: 'א-1'
 */
export const removeClassPrefix = (className: string): string => {
  return className.replace('כיתה ', '')
}

/**
 * Check if a date is today or in the future
 * Used for filtering future slots and determining available registrations
 *
 * @param dateStr - Date string in yyyy-mm-dd format
 * @returns True if date is today or future, false otherwise
 *
 * @example
 * isCurrentOrFutureDate('2025-10-15') // Returns: true (if today is before or on 2025-10-15)
 */
export const isCurrentOrFutureDate = (dateStr: string): boolean => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const slotDate = new Date(dateStr)
  slotDate.setHours(0, 0, 0, 0)

  return slotDate >= today
}

/**
 * Check if a date is exactly today
 * Used for highlighting today's row in the table
 *
 * @param dateStr - Date string in yyyy-mm-dd format
 * @returns True if date is today, false otherwise
 *
 * @example
 * isToday('2025-10-15') // Returns: true (only if today is exactly 2025-10-15)
 */
export const isToday = (dateStr: string): boolean => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const slotDate = new Date(dateStr)
  slotDate.setHours(0, 0, 0, 0)

  return slotDate.getTime() === today.getTime()
}
